import React, { useEffect, useState, useContext } from "react";
import {
  Box,
  Header,
  Heading,
  DataTable,
  Button,
  CheckBox,
  Meter,
  Text,
  ResponsiveContext,
} from "grommet";
import { Trash, Trigger, Lock, Action, Validate } from "grommet-icons";
import { onDatatableSearch } from "@common/utils";
import {
  presentToastSuccess,
  presentToastErrorContent,
  presentToastWarning,
} from "@shared/Toast";
import { AddDoor } from "./";
import DoorService from "../../services/door.service";
import { presentAlertPrompt } from "@shared/Alert";
import { useFetchHotel, useSetCurrentUser } from "./redux/hooks";
import SecondaryDoorIcon from "@shared/SecondaryDoorIcon";
import RestrictedAreaIcon from "@shared/RestrictedAreaIcon";
import ConcurrentBookingsIcon from "@shared/ConcurrentBookingsIcon";
import EditDoor from "./EditDoor";
import { useTranslation } from "react-i18next";

export default function SettingsDoorTable(props) {
  const { t } = useTranslation();
  const { title, type, doors, onRefresh } = props;
  const [checked, setChecked] = useState([]);
  const [list, setList] = useState([]);
  const [sort, setSort] = useState({ property: "name", direction: "asc" });
  const { hotel } = useFetchHotel();
  const { isLoxeAdmin } = useSetCurrentUser();
  const accessEnabled = hotel.serviceType !== "messaging";
  const contextSize = useContext(ResponsiveContext);
  const isSmall = contextSize === "small";
  const hasTTLockDoors = list.some(
    (door) => (door.lockType || hotel.lockType || "ttlock") === "ttlock",
  );

  useEffect(() => {
    setList((doors || []).filter((x) => x.type === type));
  }, [doors, type]);

  const onCheck = (event, value) => {
    if (event.target.checked) {
      setChecked([...checked, value]);
    } else {
      setChecked(checked.filter((item) => item !== value));
    }
  };

  const onCheckAll = ({ target: { checked } }) => {
    if (list.length) setChecked(checked ? list.map((datum) => datum._id) : []);
  };

  const columns = [
    {
      property: "checkbox",
      render: (datum) => (
        <CheckBox
          key={datum._id}
          checked={checked.indexOf(datum._id) !== -1}
          onChange={(e) => onCheck(e, datum._id)}
        />
      ),
      header: (
        <CheckBox
          checked={checked.length === list.length}
          indeterminate={checked.length > 0 && checked.length < list.length}
          onChange={onCheckAll}
        />
      ),
      sortable: false,
    },
    {
      property: "name",
      header: t("settingsDoors.colName"),
      primary: true,
      search: true,
      sortable: true,
      render: (datum) =>
        !datum.disabled ? (
          <Box direction="row" gap="small">
            <Text>{datum.name}</Text>
            {datum.allowConcurrentBookings && (
              <ConcurrentBookingsIcon pkey={`concurrent-${datum._id}`} />
            )}
            {isLoxeAdmin && (
              <Text size="11px" color="grey" alignSelf="center">
                {datum.pmsId}
              </Text>
            )}
            {datum.isSecondary && (
              <SecondaryDoorIcon
                pkey={`secondary-${datum._id}`}
                mainRoomName={
                  list.find((x) => x.secondaryDoor === datum._id).name
                }
              />
            )}
            {(datum.restrictedToDoorIds || []).length > 0 && (
              <RestrictedAreaIcon
                pkey={`restricted-${datum._id}`}
                restrictedRoomNames={doors
                  ?.map((x) =>
                    datum.restrictedToDoorIds.includes(x._id) ? x.name : null,
                  )
                  .filter(Boolean)}
              />
            )}
          </Box>
        ) : (
          <Text
            weight="bold"
            color="status-error"
            style={{
              textDecorationLine: "line-through",
              textDecorationStyle: "solid",
            }}
          >
            {datum.name}
          </Text>
        ),
    },
  ];
  if (type === "room")
    columns.push({
      property: "roomType",
      header: t("settingsDoors.colType"),
      search: true,
      sortable: true,
      render: (datum) =>
        !!datum.roomType ? (
          <Text truncate size="small">
            {datum.roomType.name}
          </Text>
        ) : (
          "-"
        ),
    });

  if (accessEnabled)
    columns.push({
      property: hasTTLockDoors ? "lockBattery" : "lockName",
      header: t("settingsDoors.colLock"),
      render: (datum) =>
        datum.validNoLock ? (
          <Box align="center">
            <Validate size="small" />
          </Box>
        ) : datum.lockId ? (
          (datum.lockType || hotel.lockType || "ttlock") !== "ttlock" ? (
            <Text size="small">{datum.lockName || "-"}</Text>
          ) : hotel.disableBatteryCheck ||
            !Number.isFinite(datum.lockBattery) ? (
            <Box align="center">
              <Lock size="small" />
            </Box>
          ) : (
            <Box align="center" direction="row">
              <Trigger size="small" />
              <Box width="20px" margin={{ horizontal: "5px" }}>
                <Meter
                  values={[
                    {
                      value: datum.lockBattery,
                      color:
                        datum.lockBattery >= 70
                          ? "status-ok"
                          : datum.lockBattery >= 20
                          ? "status-warning"
                          : "status-error",
                    },
                  ]}
                  thickness="small"
                  size="small"
                  background="light-6"
                />
              </Box>
              <Text size="small">
                {Math.round(datum.lockBattery * 10) / 10}%
              </Text>
            </Box>
          )
        ) : (
          "-"
        ),
      align: "center",
      sortable: true,
    });

  const handleAddDoor = (pDoors) => {
    DoorService.addDoors(pDoors).then(
      (response) => {
        onRefresh();
        presentToastSuccess("Success");
      },
      (error) => {
        presentToastErrorContent(error);
      },
    );
  };

  const handleRemoveDoor = () => {
    presentAlertPrompt({
      title: t("settingsDoors.deleteConfirmTitle"),
      message: t("settingsDoors.deleteConfirmMessage"),
      onOK: removeDoors,
      buttonOKText: t("settingsDoors.deleteButton"),
      buttonOKColor: "status-critical",
    });
  };

  const removeDoors = () => {
    DoorService.deleteDoors(checked).then(
      (response) => {
        onRefresh();
        setChecked([]);
        if (response.data.doorsInUse.length > 0)
          presentToastWarning(t("settingsDoors.doorsInUseWarning"));
        else presentToastSuccess("Success");
      },
      (error) => {
        presentToastErrorContent(error);
      },
    );
  };

  const handleToggleDoor = () => {
    presentAlertPrompt({
      title: t("settingsDoors.toggleConfirmTitle"),
      message: t("settingsDoors.toggleConfirmMessage"),
      onOK: toggleDoors,
      buttonOKText: t("settingsDoors.toggleButton"),
      buttonOKColor: "status-warning",
    });
  };

  const toggleDoors = () => {
    DoorService.toggleDoors(checked).then(
      (response) => {
        onRefresh();
        setChecked([]);
        presentToastSuccess("Success");
      },
      (error) => {
        presentToastErrorContent(error);
      },
    );
  };

  const handleEditDoor = (door) => {
    DoorService.update(door).then(
      (response) => {
        onRefresh();
        setChecked([]);
        presentToastSuccess("Success");
      },
      (error) => {
        presentToastErrorContent(error);
      },
    );
  };

  const handleEditDoors = ({ doorIds, update }) => {
    DoorService.updateMany(doorIds, update).then(
      () => {
        onRefresh();
        setChecked([]);
        presentToastSuccess("Success");
      },
      (error) => {
        presentToastErrorContent(error);
      },
    );
  };

  return (
    <Box>
      <Header
        align={isSmall ? "start" : "center"}
        direction={isSmall ? "column" : "row"}
        justify="between"
        gap="small"
        fill="horizontal"
        pad={{ vertical: isSmall ? "small" : "none" }}
      >
        <Heading level="4" margin={{ vertical: "xsmall" }}>
          {title}
        </Heading>
        <Box
          direction="row"
          wrap
          gap="small"
          align="center"
          justify={isSmall ? "start" : "end"}
          style={{ rowGap: 8 }}
        >
          {(accessEnabled || isLoxeAdmin) && (
            <AddDoor type={type} onSubmit={handleAddDoor} />
          )}
          {isLoxeAdmin && (
            <EditDoor
              type={type}
              onSubmit={handleEditDoor}
              onBulkSubmit={handleEditDoors}
              doorsToEdit={doors.filter((door) => checked.includes(door._id))}
              disabled={
                !checked.length || (type !== "room" && checked.length > 1)
              }
              allDoors={doors}
            />
          )}
          {accessEnabled && (
            <Button
              icon={<Action size="small" />}
              size="small"
              label={t("settingsDoors.enableDisable")}
              primary
              color="status-warning"
              onClick={handleToggleDoor}
              disabled={!checked.length}
            />
          )}
          <Button
            icon={<Trash size="small" />}
            size="small"
            label={t("settingsDoors.remove")}
            primary
            color="status-critical"
            onClick={handleRemoveDoor}
            disabled={!checked.length}
          />
        </Box>
      </Header>
      <Box fill="horizontal" overflow={{ horizontal: "auto" }}>
        <DataTable
          columns={columns}
          data={list}
          primaryKey="id"
          sort={sort}
          onSort={setSort}
          onSearch={(search) =>
            onDatatableSearch(
              search,
              (doors || []).filter((x) => x.type === type),
              setList,
            )
          }
        />
      </Box>
    </Box>
  );
}

SettingsDoorTable.propTypes = {};
SettingsDoorTable.defaultProps = {};
